// Eskom PARCC — match bulk-update rows to the register and diff them (R6).
//
// PURE (no React, no Firebase). Two jobs:
//
//   1. MATCH a workbook row to a live transaction. Same ladder as the CES upload
//      (matchCesUpload): exact reference → contract number → ambiguous → not
//      found. Never heuristic: an ambiguous row is reported, never guessed.
//   2. DIFF the row's payload against that transaction, cell by cell, so the
//      operator sees every `field: old → new` BEFORE anything is written. This is
//      the safety the CES upload lacks — it shows only a captured-line count —
//      and it is what makes a manual-editor-width blast radius reviewable at 300
//      rows.
//
// A row that changes nothing is `unchanged` and is not written at all.
import { refKey } from '../import/importParse';
import { canEditTransaction, type Grant } from '../../lib/permissions';
import { diffFields, TRANSACTION_FIELD_LABELS } from '../../lib/fieldDiff';
import { REVIEW_COLUMNS, isReviewColumn } from './reviewColumns';
import type { BulkUpdateRow } from './bulkUpdateParse';
import type { BulkUpdateColumn } from '../../lib/importColumns';
import type { Transaction } from '../../data/types';

export type BulkUpdateOutcome =
  | 'ready'
  | 'unchanged'
  | 'not-found'
  | 'ambiguous'
  | 'not-editable'
  | 'no-reference'
  | 'duplicate';

/** One field's before/after, as rendered in the preview. */
export interface FieldChange {
  column: BulkUpdateColumn;
  /** The transaction's current value, rendered for display ('—' when unset). */
  from: string;
  /** The value the file would write. */
  to: string;
  /** True for the review/work-product columns, which the preview flags. */
  review: boolean;
}

/** One previewed row: what it matched, what it would change, and why not. */
export interface BulkUpdateDraft {
  rowNo: number;
  referenceNo: string;
  outcome: BulkUpdateOutcome;
  /** The matched transaction — absent for not-found / ambiguous / no-reference. */
  tx?: Transaction;
  /** The row's payload, narrowed to fields that genuinely differ. */
  patch: BulkUpdateRow['patch'];
  changes: FieldChange[];
  findings: BulkUpdateRow['findings'];
  carriesFindings: boolean;
  /** True when the file's finding lines differ from the row's import-sourced ones. */
  findingsChanged: boolean;
  warnings: string[];
}

export interface BulkUpdateMatch {
  drafts: BulkUpdateDraft[];
  /** Rows that would be written (outcome === 'ready'), in file order. */
  writable: BulkUpdateDraft[];
}

/**
 * Human labels for the whitelist columns, used in the diff table. Re-exported
 * from the shared differ (`src/lib/fieldDiff.ts`) so the bulk preview and the
 * audit trail read one map, not two that can drift apart.
 */
export const COLUMN_LABELS = TRANSACTION_FIELD_LABELS;

/**
 * Diff a row's payload against its transaction, in the bulk-preview shape
 * (`{ column, from, to, review }`). Delegates to the shared `diffFields` and
 * maps back onto that shape. `valueDisplay` is suppressed — it is derived from
 * `value`, so showing both doubles every money change — and `referenceNo` is
 * never itself a change.
 */
export function diffRow(patch: BulkUpdateRow['patch'], tx: Transaction): FieldChange[] {
  return diffFields(
    tx as unknown as Record<string, unknown>,
    patch as unknown as Record<string, unknown>,
    COLUMN_LABELS,
    { skip: ['referenceNo', 'valueDisplay'] },
  ).map((c) => {
    const column = c.field as BulkUpdateColumn;
    return { column, from: c.from, to: c.to, review: isReviewColumn(column) };
  });
}

/**
 * True when the file's finding lines differ from the row's IMPORT-SOURCED ones.
 * Analyst/CES-captured findings are invisible here by design — the merge
 * preserves them regardless, so they must never make a row look "changed".
 * Ordered text compare, exactly like the import's importFindingsMatch.
 */
export function findingsDiffer(
  existing: readonly { text?: string | null; source?: string | null }[],
  incoming: readonly { text: string }[],
): boolean {
  const IMPORT_SOURCES = new Set(['imported file', 'bulk upload', 'bulk update']);
  const mine = existing
    .filter((f) => IMPORT_SOURCES.has(String(f.source ?? '').trim().toLowerCase()))
    .map((f) => (f.text ?? '').trim());
  const theirs = incoming.map((f) => f.text.trim());
  if (mine.length !== theirs.length) return true;
  return mine.some((t, i) => t !== theirs[i]);
}

export interface MatchInput {
  rows: readonly BulkUpdateRow[];
  transactions: readonly Transaction[];
  /** Findings already on each transaction, keyed by normalised reference. */
  findingsByRef: ReadonlyMap<string, readonly { text?: string | null; source?: string | null }[]>;
  grant: Grant | null;
}

/**
 * Match every parsed row, diff it, and classify the outcome.
 *
 * In-file duplicates resolve LAST-WINS, keyed on the matched transaction's `id`
 * rather than the reference string — so two rows that reach the SAME transaction
 * by different keys (one by reference, one by contract number) also collapse.
 * A ref-keyed pass, like the import's, would miss that and write twice.
 */
export function matchBulkUpdate({ rows, transactions, findingsByRef, grant }: MatchInput): BulkUpdateMatch {
  const live = transactions.filter((t) => !t.deleted);
  const byRef = new Map<string, Transaction>();
  const byContract = new Map<string, Transaction[]>();
  for (const t of live) {
    const ref = refKey(t.referenceNo);
    if (ref && !byRef.has(ref)) byRef.set(ref, t);
    const contract = refKey(t.contractNumber);
    if (contract) {
      const bucket = byContract.get(contract);
      if (bucket) bucket.push(t);
      else byContract.set(contract, [t]);
    }
  }

  const drafts: BulkUpdateDraft[] = rows.map((row) => {
    const base: BulkUpdateDraft = {
      rowNo: row.rowNo,
      referenceNo: row.referenceNo,
      outcome: 'ready',
      patch: row.patch,
      changes: [],
      findings: row.findings,
      carriesFindings: row.carriesFindings,
      findingsChanged: false,
      warnings: [...row.warnings],
    };
    const key = refKey(row.referenceNo);
    if (!key) return { ...base, outcome: 'no-reference' };

    let tx = byRef.get(key);
    if (!tx) {
      const byContractHits = byContract.get(key) ?? [];
      if (byContractHits.length === 1) {
        tx = byContractHits[0];
        base.warnings.push(
          `Matched on Contract Number to ${tx.referenceNo} — the reference itself is not in the register.`,
        );
      } else if (byContractHits.length > 1) {
        return {
          ...base,
          outcome: 'ambiguous',
          warnings: [
            ...base.warnings,
            `${byContractHits.length} transactions share this contract number — resolve it by reference instead.`,
          ],
        };
      }
    }
    if (!tx) return { ...base, outcome: 'not-found' };

    if (!canEditTransaction(tx, grant)) {
      return { ...base, tx, outcome: 'not-editable' };
    }

    const changes = diffRow(row.patch, tx);
    const findingsChanged =
      row.carriesFindings && findingsDiffer(findingsByRef.get(refKey(tx.referenceNo)) ?? [], row.findings);
    const outcome: BulkUpdateOutcome = changes.length === 0 && !findingsChanged ? 'unchanged' : 'ready';
    return { ...base, tx, outcome, changes, findingsChanged };
  });

  // Last-wins over the MATCHED transaction id.
  const lastByTx = new Map<string, number>();
  drafts.forEach((d, i) => {
    if (d.tx && d.outcome === 'ready') lastByTx.set(d.tx.id, i);
  });
  const resolved = drafts.map((d, i) => {
    if (d.tx && d.outcome === 'ready' && lastByTx.get(d.tx.id) !== i) {
      return {
        ...d,
        outcome: 'duplicate' as const,
        warnings: [
          ...d.warnings,
          'A later row in this file updates the same transaction — the last occurrence wins.',
        ],
      };
    }
    return d;
  });

  return { drafts: resolved, writable: resolved.filter((d) => d.outcome === 'ready') };
}

export { REVIEW_COLUMNS };
